import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { User, Building, Truck, ArrowLeft, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const DriverProfilePage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm();

  useEffect(() => {
    if (user) {
      setValue('businessName', user.businessName || '');
      setValue('contactPerson', user.contactPerson || '');
      setValue('serviceAreas', user.serviceAreas?.join(', ') || '');
      setValue('vehicleType', user.vehicleType || '');
      setValue('vehicleCapacity', user.vehicleCapacity || '');
      setValue('hasCrane', user.hasCrane || false);
      setValue('businessLicense', user.businessLicense || '');
    }
  }, [user, setValue]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      const result = await updateProfile({
        ...data,
        serviceAreas: data.serviceAreas.split(',').map(area => area.trim()),
        vehicleCapacity: parseInt(data.vehicleCapacity),
        businessLicense: user?.businessLicense || ''
      });
      
      if (result.success) {
        toast.success('הפרופיל עודכן בהצלחה!');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('שגיאה בעדכון הפרופיל');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-brown-700 text-lg">טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Skip link target */}
        <div id="main-content" className="sr-only" tabIndex="-1">תוכן ראשי</div>
        {/* Header */}
        <div id="navigation" className="mb-10" tabIndex="-1">
          <button
            onClick={() => navigate('/driver/dashboard')}
            className="flex items-center text-orange-600 hover:text-orange-700 mb-6 text-lg font-semibold transition-colors duration-300"
          >
            <ArrowLeft className="h-5 w-5 ml-3" />
            חזור לדשבורד
          </button>
          <h1 className="text-4xl font-bold text-brown-900 mb-4">פרופיל מוביל</h1>
          <p className="text-brown-700 text-xl">עדכן את פרטי הפרופיל שלך</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
          {/* Personal Information */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <User className="h-6 w-6 text-orange-600" />
              </div>
              פרטים אישיים
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="profile-name" className="form-label">שם מלא</label>
                <input
                  id="profile-name"
                  type="text"
                  {...register('contactPerson', { required: 'שם מלא הוא שדה חובה' })}
                  maxLength={100}
                  className="input-field"
                  placeholder="הזן את שמך המלא"
                />
                {errors.contactPerson && (
                  <p className="form-error">{errors.contactPerson.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="profile-email" className="form-label">כתובת אימייל</label>
                <input
                  id="profile-email"
                  type="email"
                  value={user.email || ''}
                  className="input-field bg-gray-100 cursor-not-allowed"
                  disabled
                  placeholder="כתובת האימייל שלך"
                />
                <p className="text-sm text-brown-600 mt-1">כתובת האימייל אינה ניתנת לשינוי</p>
              </div>

            <div>
              <label htmlFor="profile-phone" className="form-label">מספר טלפון</label>
              <input
                id="profile-phone"
                type="tel"
                value={user.phone || ''}
                className="input-field bg-gray-100 cursor-not-allowed"
                disabled
                placeholder="מספר הטלפון שלך"
              />
              <p className="text-sm text-brown-600 mt-1">מספר הטלפון אינו ניתן לשינוי</p>
            </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <Building className="h-6 w-6 text-orange-600" />
              </div>
              פרטי העסק
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="business-name" className="form-label">שם העסק</label>
                <input
                  id="business-name"
                  type="text"
                  {...register('businessName')}
                  maxLength={200}
                  className="input-field"
                  placeholder="הזן את שם העסק שלך"
                />
              </div>

              <div>
                <label htmlFor="business-license" className="form-label">רישיון עסק</label>
                <input
                  id="business-license"
                  type="text"
                  value={user.businessLicense || ''}
                  className="input-field bg-gray-100 cursor-not-allowed"
                  disabled
                  placeholder="רישיון העסק שלך"
                />
                <p className="text-sm text-brown-600 mt-1">רישיון העסק אינו ניתן לשינוי</p>
              </div>

              <div>
                <label htmlFor="service-areas" className="form-label">אזורי שירות</label>
                <input
                  id="service-areas"
                  type="text"
                  {...register('serviceAreas', { required: 'אזורי השירות הם שדה חובה' })}
                  maxLength={500}
                  className="input-field"
                  placeholder="הזן ערים מופרדות בפסיקים (לדוגמה: תל אביב, רמת גן, גבעתיים)"
                />
                {errors.serviceAreas && (
                  <p className="form-error">{errors.serviceAreas.message}</p>
                )}
                <p className="text-sm text-brown-600 mt-1">
                  הזן את הערים שבהן אתה מספק שירות, מופרדות בפסיקים
                </p>
              </div>
            </div>
          </div>

          {/* Vehicle Information */}
          <div className="card hover-lift">
            <h2 className="text-2xl font-bold text-brown-900 mb-8 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-4">
                <Truck className="h-6 w-6 text-orange-600" />
              </div>
              פרטי הרכב
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="vehicle-type" className="form-label">סוג רכב</label>
                <select
                  id="vehicle-type"
                  {...register('vehicleType', { required: 'סוג הרכב הוא שדה חובה' })}
                  className="input-field"
                >
                  <option value="">בחר סוג רכב</option>
                  <option value="truck">משאית</option>
                  <option value="pickup">טנדר</option>
                  <option value="van">ואן</option>
                  <option value="other">אחר</option>
                </select>
                {errors.vehicleType && (
                  <p className="form-error">{errors.vehicleType.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="vehicle-capacity" className="form-label">קיבולת רכב (בטון)</label>
                <input
                  id="vehicle-capacity"
                  type="number"
                  {...register('vehicleCapacity', {
                    required: 'קיבולת הרכב היא שדה חובה',
                    min: { value: 0.5, message: 'קיבולת הרכב חייבת להיות לפחות 0.5 טון' },
                    max: { value: 50, message: 'קיבולת הרכב לא יכולה לעלות על 50 טון' }
                  })}
                  className="input-field"
                  placeholder="הזן קיבולת בטון"
                  min="0.5"
                  max="50"
                  step="0.1"
                />
                {errors.vehicleCapacity && (
                  <p className="form-error">{errors.vehicleCapacity.message}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <label className="flex items-center space-x-3 space-x-reverse">
                <input
                  type="checkbox"
                  {...register('hasCrane')}
                  className="h-5 w-5 text-orange-600 focus:ring-orange-500 border-brown-300 rounded"
                />
                <span className="text-lg text-brown-700">יש לי מנוף</span>
              </label>
            </div>
          </div>

          {/* Contact Information section removed as requested */}

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary text-xl px-16 py-4 disabled:opacity-50 disabled:cursor-not-allowed hover-lift"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                  שומר...
                </div>
              ) : (
                <div className="flex items-center">
                  <Save className="h-5 w-5 ml-2" />
                  שמור שינויים
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DriverProfilePage;
